import { useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';

export default function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [registerAsOwner, setRegisterAsOwner] = useState(true);
  const [portfolioToken, setPortfolioToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = { email, password };
      
      if (isRegister) {
        payload.role = registerAsOwner ? 'owner' : 'agent';
        if (!registerAsOwner) {
          if (!portfolioToken) {
            setError('Portfolio token is required');
            toast.error('Portfolio token is required');
            setLoading(false);
            return;
          }
          payload.portfolio_token = portfolioToken;
        }
      }
      
      const response = await api.post(endpoint, payload);
      
      toast.success(isRegister ? 'Account created successfully!' : 'Welcome back!');
      onLogin(response.data.user, response.data.access_token);
    } catch (err) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email: resetEmail });
      toast.success('Password reset link sent! Check your email.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      toast.error('Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="login-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/logo.png" alt="Unit Relay" className="auth-logo" data-testid="auth-logo" />
          <p data-testid="auth-subtitle">Real Estate Portfolio Management</p>
        </div>

        {showForgotPassword ? (
          <form className="auth-form" onSubmit={handleForgotPassword} data-testid="forgot-password-form">
            <div className="form-group">
              <label htmlFor="resetEmail">Email Address</label>
              <input
                id="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                placeholder="your@email.com"
                data-testid="reset-email-input"
              />
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              data-testid="send-reset-link-button"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
              Remember your password?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmail('');
                  setError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                data-testid="back-to-login-button"
              >
                Back to login
              </button>
            </p>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit} data-testid="auth-form">
          {isRegister && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer', marginBottom: '8px' }}>
                <input
                  type="radio"
                  checked={registerAsOwner}
                  onChange={() => setRegisterAsOwner(true)}
                  data-testid="register-as-owner-radio"
                />
                Register as Owner (create new portfolio)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={!registerAsOwner}
                  onChange={() => setRegisterAsOwner(false)}
                  data-testid="register-as-agent-radio"
                />
                Join existing portfolio (agent)
              </label>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
              data-testid="email-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              data-testid="password-input"
            />
          </div>

          {isRegister && !registerAsOwner && (
            <div className="form-group">
              <label htmlFor="portfolioToken">Portfolio Token</label>
              <input
                id="portfolioToken"
                type="text"
                value={portfolioToken}
                onChange={(e) => setPortfolioToken(e.target.value.toUpperCase())}
                required
                placeholder="Enter 8-character token"
                maxLength={8}
                data-testid="portfolio-token-input"
                style={{ textTransform: 'uppercase' }}
              />
              <small style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                Ask your portfolio owner for the token
              </small>
            </div>
          )}

          {error && <div className="error-message" data-testid="error-message">{error}</div>}

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </button>

          {!isRegister && (
            <p style={{ textAlign: 'right', marginTop: '-8px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline'
                }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </p>
          )}

          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => {
                setIsRegister(!isRegister);
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              data-testid="toggle-auth-button"
            >
              {isRegister ? 'Sign in' : 'Create account'}
            </button>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
